import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { UserSession } from '../types';
import { supabase } from '../services/supabaseClient';
import { useSingleDeviceSession } from '../hooks/useSingleDeviceSession';
import { claimDeviceSession, clearDeviceSession } from '../services/sessionGuard';
import { logLoginEvent, logLoginFailure } from '../services/loginTelemetry';

interface AuthContextType {
  user: UserSession;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearPasswordReset: () => void;
  loading: boolean;
  /** Current access token — use this instead of calling supabase.auth.getSession() */
  accessToken: string | null;
  /**
   * Relit forfait / abonnement / accès depuis la base (après un paiement). Sans lui, un forfait
   * payé n'apparaissait qu'au prochain login ou rechargement complet.
   */
  refreshEnterprise?: () => Promise<void>;
  /**
   * Forfait / accès de l'entreprise SÉLECTIONNÉE (utilisateur de plusieurs entreprises). null tant
   * qu'aucune n'est sélectionnée, si l'utilisateur n'en est pas membre ou si le serveur ne sait pas
   * répondre : on garde alors ceux de `user` (1re adhésion), comme avant.
   */
  activeAccess?: EnterpriseAccess | null;
  selectEnterprise?: (enterpriseId: string | null) => Promise<void>;
}

export interface EnterpriseAccess {
  enterprise_id: string;
  plan: UserSession['plan'];
  entitlement_overrides: UserSession['entitlement_overrides'];
  access_mode: UserSession['access_mode'];
  mask_candidate_contacts: UserSession['mask_candidate_contacts'];
  subscription: UserSession['subscription'];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Dérive si l'utilisateur doit être forcé vers /reset-password.
// `alreadyCleared` = ce tab a déjà confirmé l'effacement du mot de passe → on n'impose
// PLUS jamais le reset, même si un JWT/app_metadata périmé porte encore must_change_password=true
// (sinon TOKEN_REFRESHED/USER_UPDATED ré-imposait /reset-password en pleine session — bug du popup).
export function deriveNeedsPasswordReset(mustChange: boolean, alreadyCleared: boolean): boolean {
  return alreadyCleared ? false : mustChange;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]             = useState<UserSession>({ loggedIn: false });
  const [loading, setLoading]       = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // Guard against concurrent getSession() calls — only one at a time
  const sessionFetching = useRef(false);
  // Verrou local « le reset a déjà été effacé dans ce tab » → empêche tout event de session
  // (TOKEN_REFRESHED / USER_UPDATED / restore) de ré-imposer /reset-password avec un JWT périmé.
  // Démarre à false → le tout premier login d'un invité (must_change_password=true) est bien forcé.
  const passwordResetCleared = useRef(false);
  const [activeAccess, setActiveAccess] = useState<EnterpriseAccess | null>(null);
  const activeEnterpriseRef = useRef<string | null>(null);

  // Load user role and enterprise from enterprises_team via RPC with localStorage cache.
  // Retries transient network drops ("La connexion réseau a été perdue" / "Load failed"),
  // which happen when the browser connection pool is saturated; falls back to the cache.
  const loadUserRoleAndEnterprise = async (userId: string, email?: string) => {
    const MAX_ATTEMPTS = 3;
    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const { data, error } = await supabase.rpc('get_user_enterprise', {
          p_auth_user_id: userId,
        });
        if (error) { lastError = error; throw error; }

        const result = {
          role: data?.role,
          enterprise_id: data?.enterprise_id,
          plan: data?.plan ?? null,
          entitlement_overrides: data?.entitlement_overrides ?? null,
          access_mode: data?.access_mode ?? null,
          mask_candidate_contacts: data?.mask_candidate_contacts ?? null,
          subscription: data?.subscription ?? null,
        };
        // Cache keyed by userId; clears any stale id when the user has no enterprise.
        // The plan IS cached (#13): on a cache-fallback the user keeps their last known plan
        // instead of being locked down to freemium by a transient network drop. Overrides are
        // not cached. null here means « lu, absent » → freemium (fail-closed).
        cacheRoleAndEnterprise(userId, result.role, result.enterprise_id, result.plan);
        return result;
      } catch (err) {
        lastError = err;
        if (attempt < MAX_ATTEMPTS) {
          await new Promise(r => setTimeout(r, attempt * 400)); // 400ms, 800ms backoff
          continue;
        }
      }
    }

    // All attempts failed (likely a transient network drop) → fall back to cached values
    console.warn('[AuthContext] get_user_enterprise failed after retries:',
      (lastError as { message?: string })?.message ?? lastError);
    return getCachedRoleAndEnterprise(userId);
  };

  // Cache keyed PER user — avoids one account inheriting another's enterprise on a
  // shared browser, and lets us drop a stale id when the user has no enterprise.
  const entKey  = (uid: string) => `tc-cached-enterprise-id:${uid}`;
  const roleKey = (uid: string) => `tc-cached-role:${uid}`;
  const planKey = (uid: string) => `tc-cached-plan:${uid}`;

  const cacheRoleAndEnterprise = (uid: string, role?: string, enterprise_id?: string, plan?: UserSession['plan']) => {
    try {
      // Drop the legacy non-keyed cache so an old stale value can't resurface.
      localStorage.removeItem('tc-cached-enterprise-id');
      localStorage.removeItem('tc-cached-role');
      if (enterprise_id) {
        localStorage.setItem(entKey(uid), JSON.stringify(enterprise_id));
        localStorage.setItem(roleKey(uid), JSON.stringify(role ?? null));
        localStorage.setItem(planKey(uid), JSON.stringify(plan ?? null)); // null = lu, absent (#13)
      } else {
        // No enterprise (e.g. after a DB change) → clear, never keep a dead id.
        localStorage.removeItem(entKey(uid));
        localStorage.removeItem(roleKey(uid));
        localStorage.removeItem(planKey(uid));
      }
    } catch {}
  };

  // Get cached role/enterprise or defaults (keyed by userId)
  const getCachedRoleAndEnterprise = (uid?: string) => {
    try {
      if (!uid) return { role: undefined, enterprise_id: undefined, plan: null, entitlement_overrides: null, access_mode: null, mask_candidate_contacts: null, subscription: null };
      const enterprise_id = localStorage.getItem(entKey(uid));
      const role = localStorage.getItem(roleKey(uid));
      const cachedPlan = localStorage.getItem(planKey(uid));
      // Fallback = the read is OVER (RPC failed after retries): the plan is the last cached one,
      // or null (« lu, absent » → freemium, fail-closed #13). Never undefined here — undefined
      // (« pas encore lu » → pending, no blocking) only exists while the RPC is still running.
      const plan = cachedPlan ? JSON.parse(cachedPlan) : null;
      return {
        enterprise_id: enterprise_id ? JSON.parse(enterprise_id) : undefined,
        role: role ? JSON.parse(role) : undefined,
        plan: typeof plan === 'string' ? plan as UserSession['plan'] : null,
        entitlement_overrides: null,
        access_mode: null,
        mask_candidate_contacts: null,
        subscription: null,
      };
    } catch {
      return { role: undefined, enterprise_id: undefined, plan: null, entitlement_overrides: null, access_mode: null, mask_candidate_contacts: null, subscription: null };
    }
  };

  const clearCachedRoleAndEnterprise = (uid?: string) => {
    try {
      if (uid) { localStorage.removeItem(entKey(uid)); localStorage.removeItem(roleKey(uid)); localStorage.removeItem(planKey(uid)); }
      localStorage.removeItem('tc-cached-enterprise-id'); // legacy non-keyed
      localStorage.removeItem('tc-cached-role');
    } catch {}
  };

  // Restore session on mount
  useEffect(() => {
    if (sessionFetching.current) return;
    sessionFetching.current = true;

    const sessionTimeout = setTimeout(() => {
      if (sessionFetching.current) {
        setLoading(false);
        sessionFetching.current = false;
      }
    }, 8000);

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      clearTimeout(sessionTimeout);
      if (session?.user) {
        // Use cached values first for fast render
        const cached = getCachedRoleAndEnterprise(session.user.id);
        setUser({
          loggedIn: true,
          email: session.user.email,
          userId: session.user.id,
          id: session.user.id,
          role: cached.role,
          enterprise_id: cached.enterprise_id,
          needsPasswordReset: deriveNeedsPasswordReset(!!(session.user.app_metadata as any)?.must_change_password, passwordResetCleared.current),
        });
        setAccessToken(session.access_token ?? null);

        // Load fresh data in parallel without blocking
        loadUserRoleAndEnterprise(session.user.id, session.user.email).then(({ role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }) => {
          setUser(prev => ({
            ...prev,
            role,
            enterprise_id,
            plan,
            entitlement_overrides,
            access_mode,
            mask_candidate_contacts,
            subscription
          }));
        }).catch(err => {
          console.warn('[AuthContext] Background role load failed:', err);
          setUser(prev => (prev.plan === undefined ? { ...prev, plan: null } : prev)); // lecture finie (#13)
        });
      }
      setLoading(false);
      sessionFetching.current = false;
    }).catch((err) => {
      clearTimeout(sessionTimeout);
      console.error('[AuthContext] getSession failed:', err);
      setLoading(false);
      sessionFetching.current = false;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session?.user) {
        const cached = getCachedRoleAndEnterprise(session.user.id);
        setUser({
          loggedIn: true,
          email: session.user.email,
          userId: session.user.id,
          id: session.user.id,
          role: cached.role,
          enterprise_id: cached.enterprise_id,
          needsPasswordReset: true
        });
        setAccessToken(session.access_token ?? null);
        // Load fresh data in background
        loadUserRoleAndEnterprise(session.user.id, session.user.email).then(({ role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }) => {
          setUser(prev => ({ ...prev, role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }));
        }).catch(err => {
          console.warn('[AuthContext] Background role load failed:', err);
          setUser(prev => (prev.plan === undefined ? { ...prev, plan: null } : prev)); // lecture finie (#13)
        });
      } else if (session?.user) {
        const cached = getCachedRoleAndEnterprise(session.user.id);
        setUser({
          loggedIn: true,
          email: session.user.email,
          userId: session.user.id,
          id: session.user.id,
          role: cached.role,
          enterprise_id: cached.enterprise_id,
          needsPasswordReset: deriveNeedsPasswordReset(!!(session.user.app_metadata as any)?.must_change_password, passwordResetCleared.current),
        });
        setAccessToken(session.access_token ?? null);
        // Load fresh data in background
        loadUserRoleAndEnterprise(session.user.id, session.user.email).then(({ role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }) => {
          setUser(prev => ({ ...prev, role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }));
        }).catch(err => {
          console.warn('[AuthContext] Background role load failed:', err);
          setUser(prev => (prev.plan === undefined ? { ...prev, plan: null } : prev)); // lecture finie (#13)
        });
      } else {
        setUser({ loggedIn: false });
        setAccessToken(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Télémétrie des tentatives échouées (rapport sécurité superadmin).
        // Fire-and-forget : ne modifie ni le message ni le flux d'erreur du login.
        void logLoginFailure(email, error.message || 'invalid_credentials');
        throw error;
      }
      // Ensure session is established in the client before returning
      // This prevents RLS 403 errors on subsequent API calls
      const { data: { session } } = await supabase.auth.getSession();
      // Update user state immediately with cached role/enterprise to enable fast redirect
      // Then load fresh data in background
      if (session?.user) {
        // Session mono-appareil : réclamer cet appareil (garde applicative via
        // user_sessions + Realtime → enforceKick éjecte les autres navigateurs).
        // On ne révoque PLUS les autres sessions côté serveur (signOut
        // scope:'others') : ça reapait aussi les sessions OAuth (ChatGPT/MCP)
        // → GoTrue 403 session_not_found sur le token ChatGPT dès qu'un login web
        // arrivait sur le même compte.
        // claimDeviceSession ne jette jamais (fail-safe) → ne peut pas casser le login.
        await claimDeviceSession(session.user.id);
        // Télémétrie connexion (géoloc IP → détection fraude superadmin).
        // Fire-and-forget : ne jamais bloquer/casser le login.
        void logLoginEvent(session);
        const cached = getCachedRoleAndEnterprise(session.user.id);
        setUser({
          loggedIn: true,
          email: session.user.email,
          userId: session.user.id,
          id: session.user.id,
          role: cached.role,
          enterprise_id: cached.enterprise_id,
          needsPasswordReset: !!(session.user.app_metadata as any)?.must_change_password,
        });
        setAccessToken(session.access_token ?? null);

        // Load fresh role/enterprise in background without blocking
        loadUserRoleAndEnterprise(session.user.id, session.user.email).then(({ role, enterprise_id, plan, entitlement_overrides, access_mode, mask_candidate_contacts, subscription }) => {
          setUser(prev => ({
            ...prev,
            role,
            enterprise_id,
            plan,
            entitlement_overrides,
            access_mode,
            mask_candidate_contacts,
            subscription
          }));
        }).catch(err => {
          console.warn('[AuthContext] Background role load failed:', err);
          setUser(prev => (prev.plan === undefined ? { ...prev, plan: null } : prev)); // lecture finie (#13)
        });
      }
    } finally {
      // Ensure loading is false so PrivateRoute doesn't block redirect
      setLoading(false);
    }
  };

  const logout = async () => {
    clearDeviceSession();
    clearCachedRoleAndEnterprise(user?.userId);
    await supabase.auth.signOut();
    // Ré-arme la garde : sur navigateur partagé, un AUTRE compte (must_change_password=true)
    // doit de nouveau être forcé au reset à sa prochaine connexion.
    passwordResetCleared.current = false;
    activeEnterpriseRef.current = null;
    setActiveAccess(null);
    setUser({ loggedIn: false });
  };

  const clearPasswordReset = () => {
    // Pose le verrou local AVANT toute mise à jour d'état : aucun event de session ultérieur
    // (USER_UPDATED émis par updateUser, TOKEN_REFRESHED) ne pourra ré-imposer le reset dans ce tab.
    passwordResetCleared.current = true;
    setUser(prev => ({ ...prev, needsPasswordReset: false }));
  };

  // Accès de l'entreprise sélectionnée (lecture seule, bornée côté base à l'appartenance).
  // Fonction absente (base non migrée) ou non-membre → null → forfait de la 1re adhésion, comme avant.
  const selectEnterprise = useCallback(async (enterpriseId: string | null): Promise<void> => {
    activeEnterpriseRef.current = enterpriseId;
    if (!enterpriseId) { setActiveAccess(null); return; }
    try {
      const { data, error } = await supabase.rpc('get_enterprise_access', { p_enterprise_id: enterpriseId });
      if (activeEnterpriseRef.current !== enterpriseId) return;      // sélection changée entre-temps
      setActiveAccess(!error && data ? data as EnterpriseAccess : null);
    } catch {
      if (activeEnterpriseRef.current === enterpriseId) setActiveAccess(null);
    }
  }, []);

  // Relecture ciblée après paiement. Volontairement SANS repli sur le cache : en cas d'échec réseau
  // on garde l'état affiché plutôt que d'effacer le forfait (le cache ne porte pas le plan).
  const refreshEnterprise = useCallback(async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const { data, error } = await supabase.rpc('get_user_enterprise', { p_auth_user_id: session.user.id });
    if (error || !data) return;
    setUser(prev => (prev.loggedIn ? {
      ...prev,
      role: data.role,
      enterprise_id: data.enterprise_id,
      plan: data.plan ?? null,
      entitlement_overrides: data.entitlement_overrides ?? null,
      access_mode: data.access_mode ?? null,
      mask_candidate_contacts: data.mask_candidate_contacts ?? null,
      subscription: data.subscription ?? null,
    } : prev));
    if (activeEnterpriseRef.current) await selectEnterprise(activeEnterpriseRef.current);
  }, [selectEnterprise]);

  // Éjection mono-appareil (tous les rôles sauf superadmin).
  useSingleDeviceSession(user);

  return (
    <AuthContext.Provider value={{ user, login, logout, clearPasswordReset, loading, accessToken, refreshEnterprise, activeAccess, selectEnterprise }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
